import * as React from "react";
import isEmpty from "lodash/isEmpty";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import usePermissions from "hooks/usePermissions";
import {
  getGeneralEventAttributeRenderers,
  MessageRenderer,
  TagsRenderer
} from "components/events/events/ColumnRenderers";
import ChipsCell from "components/common/ChipsCell";
import useAppendFilter from "components/common/PaginatedEntityTable/hooks/useAppendFilter";
import { RiskScoreBadge } from "security-app/components/common";
import { SECURITY_EVENT_STATUSES } from "security-app/hooks/api/securityEventsAPI.types";
import { useGetEventDefinitionDetailsBatched, useGetSecurityEvent } from "security-app/hooks/useSecurityEventsAPI";
import { useGetAssetsByIds } from "security-app/hooks/useAssetsAPI";
import EventDefinitionLink from "components/events/events/EventDefinitionLink";
import Spinner from "components/common/Spinner";
import useMitreNameIndex, {
  formatMitreCategory
} from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreNameIndex";
import { categoryKey } from "security-app/components/SecurityEvents/Alerts/Constants";
import Label from "components/bootstrap/Label";
import { Row } from "common/components";
import EventFields from "components/events/events/EventFields";
import { COLUMN_WIDTH } from "components/common/EntityDataTable/Constants";
import OwnerCell from "./OwnerCell";
import { AssociatedAssetsCell, InvestigationsCell } from "./AssocAssetsAndInvestigationsCell";
export { OwnerCell };
export const CategoryCell = ({ category, withTitle = false }) => {
  const { nameById, isLoadingNames } = useMitreNameIndex();
  if (isLoadingNames) return /* @__PURE__ */ React.createElement(Spinner, null);
  const title = formatMitreCategory(category.toLocaleUpperCase(), nameById);
  return /* @__PURE__ */ React.createElement("span", { title: withTitle ? title : void 0 }, title);
};
const EventDefinitionRenderer = ({ eventDefinitionId }) => {
  const { isPermitted } = usePermissions();
  const canReadEventDefinition = isPermitted(`eventdefinitions:read:${eventDefinitionId}`);
  const { eventDefinition, loadingEventDefinition } = useGetEventDefinitionDetailsBatched(
    canReadEventDefinition ? eventDefinitionId : null
  );
  if (!canReadEventDefinition) return /* @__PURE__ */ React.createElement("i", null, eventDefinitionId);
  const title = eventDefinition == null ? void 0 : eventDefinition.title;
  return !loadingEventDefinition && /* @__PURE__ */ React.createElement(EventDefinitionLink, { id: eventDefinitionId, title });
};
const AssociatedInvestigationsRenderer = ({ eventId }) => {
  const { securityEvent, loadingSecurityEvents } = useGetSecurityEvent(eventId);
  if (loadingSecurityEvents) return null;
  return /* @__PURE__ */ React.createElement(InvestigationsCell, { investigations: (securityEvent == null ? void 0 : securityEvent.associated_investigations) || void 0 });
};
const AssociatedAssetsRenderer = ({ assetIds }) => {
  const { t } = useTranslation("enterprise_security_events");
  const { isPermitted } = usePermissions();
  const canReadAssets = isPermitted("asset:read");
  const { assets, fetchingAssets } = useGetAssetsByIds(canReadAssets ? assetIds != null ? assetIds : [] : []);
  if (!canReadAssets || !(assetIds == null ? void 0 : assetIds.length)) {
    return null;
  }
  if (fetchingAssets) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!(assets == null ? void 0 : assets.length)) {
    return /* @__PURE__ */ React.createElement("em", null, t("column_renderers.none"));
  }
  return /* @__PURE__ */ React.createElement(AssociatedAssetsCell, { assets: assets || void 0 });
};
const stripPrefix = (category) => category.replace(/^attack\./, "");
const isTechnique = (category) => /^t\d+/.test(category.toLocaleLowerCase());
export const TacticTechnique = ({ value, withTitle = true }) => /* @__PURE__ */ React.createElement(CategoryCell, { withTitle, category: stripPrefix(value) });
const techniqueIds = (entity) => {
  var _a, _b;
  const techniques = /* @__PURE__ */ new Set();
  const add = (raw) => {
    const stripped = stripPrefix(raw);
    if (isTechnique(stripped)) techniques.add(stripped.toLocaleUpperCase());
  };
  Object.entries((_a = entity.fields) != null ? _a : {}).filter(([k]) => k.startsWith("sigma_rule_tag_")).forEach(([_k, value]) => add(value));
  ((_b = entity.tactics_techniques) != null ? _b : []).forEach(add);
  return [...techniques];
};
export const MitreCategoryRenderer = ({ entity }) => /* @__PURE__ */ React.createElement(ChipsCell, { items: techniqueIds(entity), renderItemContent: (t) => /* @__PURE__ */ React.createElement(TacticTechnique, { value: t }) });
export const ClickableTacticsTechniques = ({ items, filterKey }) => {
  const appendFilter = useAppendFilter(filterKey);
  const { nameById } = useMitreNameIndex();
  return /* @__PURE__ */ React.createElement(
    ChipsCell,
    {
      items,
      renderItemContent: (id) => /* @__PURE__ */ React.createElement(TacticTechnique, { value: id, withTitle: false }),
      onItemClick: appendFilter,
      itemLabel: "tactic/technique",
      itemTitle: (id) => formatMitreCategory(stripPrefix(id).toLocaleUpperCase(), nameById)
    }
  );
};
const MessageColumn = styled.div`
  min-width: 0;
`;
const DetectionChainLabel = styled(Label)`
  flex-shrink: 0;
  margin-left: auto;
`;
const MessageWithLabelRenderer = ({
  message,
  eventId,
  partOfDetectionChain = false
}) => {
  const { t } = useTranslation("enterprise_security_events");
  return /* @__PURE__ */ React.createElement(Row, { $align: "center", $gap: "md" }, /* @__PURE__ */ React.createElement(MessageColumn, null, /* @__PURE__ */ React.createElement(MessageRenderer, { message, eventId })), partOfDetectionChain && /* @__PURE__ */ React.createElement(DetectionChainLabel, { bsStyle: "warning", title: t("column_renderers.part_of_detection_chain") }, t("column_renderers.detection_chain")));
};
const FieldsRenderer = ({ fields }) => {
  const { t } = useTranslation("enterprise_security_events");
  return isEmpty(fields) ? /* @__PURE__ */ React.createElement("em", null, t("column_renderers.no_additional_fields")) : /* @__PURE__ */ React.createElement(EventFields, { fields });
};
const RiskScoreRenderer = (scores) => (scores == null ? void 0 : scores.normalized_risk) ? /* @__PURE__ */ React.createElement(RiskScoreBadge, { riskScore: scores == null ? void 0 : scores.normalized_risk }) : /* @__PURE__ */ React.createElement("span", null);
const customColumnRenderers = {
  attributes: {
    ...getGeneralEventAttributeRenderers(),
    message: {
      staticWidth: COLUMN_WIDTH.description,
      renderCell: (message, event) => /* @__PURE__ */ React.createElement(
        MessageWithLabelRenderer,
        {
          message,
          eventId: event.id,
          partOfDetectionChain: event.part_of_detection_chain
        }
      )
    },
    scores: {
      staticWidth: "matchHeader",
      renderCell: (scores) => RiskScoreRenderer(scores),
      textAlign: "right"
    },
    owner: {
      renderCell: (owner) => /* @__PURE__ */ React.createElement(OwnerCell, { owner }),
      staticWidth: COLUMN_WIDTH.user
    },
    status: {
      renderCell: (status) => /* @__PURE__ */ React.createElement("span", null, SECURITY_EVENT_STATUSES[status]),
      staticWidth: 110
    },
    event_definition_id: {
      staticWidth: COLUMN_WIDTH.reference,
      renderCell: (eventDefinitionId) => /* @__PURE__ */ React.createElement(EventDefinitionRenderer, { eventDefinitionId })
    },
    associated_investigations: {
      renderCell: (_, { event_id }) => /* @__PURE__ */ React.createElement(AssociatedInvestigationsRenderer, { eventId: event_id }),
      staticWidth: COLUMN_WIDTH.reference
    },
    associated_assets: {
      renderCell: (assetIds) => /* @__PURE__ */ React.createElement(AssociatedAssetsRenderer, { assetIds }),
      staticWidth: 220
    },
    [categoryKey]: {
      renderCell: (_field, entity) => /* @__PURE__ */ React.createElement(MitreCategoryRenderer, { entity }),
      staticWidth: COLUMN_WIDTH.tags
    },
    fields: {
      renderCell: (fields) => /* @__PURE__ */ React.createElement(FieldsRenderer, { fields }),
      staticWidth: 400
    },
    tags: {
      renderCell: (tags) => /* @__PURE__ */ React.createElement(TagsRenderer, { tags }),
      staticWidth: COLUMN_WIDTH.tags
    }
  }
};
export const alertsListColumnRenderers = {
  ...customColumnRenderers,
  attributes: {
    ...customColumnRenderers.attributes,
    [categoryKey]: {
      ...customColumnRenderers.attributes[categoryKey],
      renderCell: (_field, entity) => /* @__PURE__ */ React.createElement(ClickableTacticsTechniques, { items: techniqueIds(entity), filterKey: categoryKey })
    }
  }
};
export default customColumnRenderers;
